import React, { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Table, Form, InputGroup, Button, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

const dropdownStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  zIndex: 20,
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  minWidth: "180px",
  maxHeight: "220px",
  overflowY: "auto",
  padding: "6px 0",
};

const dropdownItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "6px 12px",
  cursor: "pointer",
  fontSize: "0.8rem",
  color: "#334155",
  transition: "background 0.15s",
};

const filterBtnStyle = (active) => ({
  padding: "4px 10px",
  fontSize: "0.75rem",
  borderRadius: "6px",
  border: active ? "1px solid #6366f1" : "1px solid #e2e8f0",
  background: active ? "#eef2ff" : "#f8fafc",
  color: active ? "#4f46e5" : "#64748b",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  whiteSpace: "nowrap",
  fontWeight: active ? "600" : "400",
});

const MultiSelectDropdown = ({ label, options, selected, onChange, renderOption }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        style={filterBtnStyle(selected.length > 0)}
        onClick={() => setOpen(!open)}
        type="button"
      >
        {label}
        {selected.length > 0 && (
          <Badge pill bg="primary" style={{ fontSize: "0.65rem", lineHeight: "1" }}>
            {selected.length}
          </Badge>
        )}
        <FontAwesomeIcon
          icon={["fas", "chevron-down"]}
          style={{ fontSize: "8px", marginLeft: "2px" }}
        />
      </button>
      {open && (
        <div style={dropdownStyle}>
          {selected.length > 0 && (
            <div
              style={{ ...dropdownItemStyle, color: "#6366f1", borderBottom: "1px solid #f1f5f9" }}
              onClick={() => { onChange([]); setOpen(false); }}
            >
              Clear all
            </div>
          )}
          {options.map((opt) => (
            <div
              key={opt}
              style={{
                ...dropdownItemStyle,
                background: selected.includes(opt) ? "#f1f5f9" : "transparent",
              }}
              onClick={() => toggle(opt)}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                readOnly
                style={{ accentColor: "#6366f1", cursor: "pointer" }}
              />
              {renderOption ? renderOption(opt) : opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const XSurveyFeed = ({
  feedbackData = [],
  isLoading = false,
  onRefresh,
  globalDateRange,
  subdomain = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [commentFilter, setCommentFilter] = useState("all"); // "all" | "with" | "without"

  // Derive unique ratings and agents from data
  const { uniqueRatings, uniqueAgents } = useMemo(() => {
    if (!Array.isArray(feedbackData)) return { uniqueRatings: [], uniqueAgents: [] };
    const ratings = new Set();
    const agents = new Set();
    feedbackData.forEach((item) => {
      if (item.rating !== undefined && item.rating !== null) ratings.add(String(item.rating));
      const name = item.agent_name || "";
      if (name) agents.add(name);
    });
    return {
      uniqueRatings: Array.from(ratings).sort((a, b) => Number(a) - Number(b)),
      uniqueAgents: Array.from(agents).sort((a, b) => a.localeCompare(b)),
    };
  }, [feedbackData]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(feedbackData)) return [];

    let filtered = feedbackData.filter((item) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          (item.ticket_id && String(item.ticket_id).toLowerCase().includes(term)) ||
          (item.agent_name && item.agent_name.toLowerCase().includes(term)) ||
          (item.requester_name && item.requester_name.toLowerCase().includes(term)) ||
          (item.comment && item.comment.toLowerCase().includes(term)) ||
          (item.rating && String(item.rating).toLowerCase().includes(term));
        if (!matchesSearch) return false;
      }

      // Rating filter
      if (selectedRatings.length > 0 && !selectedRatings.includes(String(item.rating))) {
        return false;
      }

      // Agent filter
      if (selectedAgents.length > 0 && !selectedAgents.includes(item.agent_name || "")) {
        return false;
      }

      // Comment filter
      if (commentFilter === "with" && (!item.comment || !item.comment.trim())) return false;
      if (commentFilter === "without" && item.comment && item.comment.trim()) return false;

      return true;
    });

    filtered.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "ticket_id":
          valA = String(a.ticket_id || "");
          valB = String(b.ticket_id || "");
          break;
        case "rating":
          valA = Number(a.rating) || 0;
          valB = Number(b.rating) || 0;
          break;
        case "requester":
          valA = (a.requester_name || "").toLowerCase();
          valB = (b.requester_name || "").toLowerCase();
          break;
        case "agent":
          valA = (a.agent_name || "").toLowerCase();
          valB = (b.agent_name || "").toLowerCase();
          break;
        case "date":
        default:
          valA = new Date(a.received_date || a.date || 0).getTime();
          valB = new Date(b.received_date || b.date || 0).getTime();
          break;
      }
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [feedbackData, searchTerm, sortField, sortDirection, selectedRatings, selectedAgents, commentFilter]);

  const activeFilterCount = (selectedRatings.length > 0 ? 1 : 0) + (selectedAgents.length > 0 ? 1 : 0) + (commentFilter !== "all" ? 1 : 0);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "sort";
    return sortDirection === "asc" ? "sort-up" : "sort-down";
  };

  const getRatingBadge = (rating) => {
    const numRating = Number(rating);
    if (isNaN(numRating)) return <Badge bg="secondary">{rating || "N/A"}</Badge>;
    if (numRating >= 4) return <Badge bg="success">{numRating}</Badge>;
    if (numRating >= 3) return <Badge bg="warning" text="dark">{numRating}</Badge>;
    return <Badge bg="danger">{numRating}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const clearAllFilters = () => {
    setSelectedRatings([]);
    setSelectedAgents([]);
    setCommentFilter("all");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    >
      <Card
        className="border-0"
        style={{
          borderRadius: "16px",
          overflow: "visible",
          background: "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
        }}
      >
        <Card.Header
          className="pb-2 pt-2 px-3 bg-transparent border-0"
          style={{ borderBottom: "1px solid rgba(226, 232, 240, 0.8)" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
                }}
              >
                <FontAwesomeIcon
                  icon={["fas", "comments"]}
                  style={{ color: "#475569", fontSize: "16px" }}
                />
              </div>
              <div>
                <h5
                  className="mb-1 fw-bold"
                  style={{ fontSize: "0.95rem", color: "#0f172a" }}
                >
                  Customer Feedback
                </h5>
                <span
                  className="d-block"
                  style={{ fontSize: "0.75rem", color: "#64748b" }}
                >
                  {filteredData.length} response{filteredData.length !== 1 ? "s" : ""}
                  {activeFilterCount > 0 && ` (filtered)`}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ maxWidth: "220px" }}>
                <InputGroup.Text style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <FontAwesomeIcon icon={["fas", "search"]} style={{ color: "#94a3b8", fontSize: "12px" }} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search feedback..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ border: "1px solid #e2e8f0", borderLeft: "none", fontSize: "0.8rem" }}
                />
              </InputGroup>
              {onRefresh && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isLoading}
                  style={{ borderColor: "#e2e8f0", color: "#64748b" }}
                >
                  <FontAwesomeIcon icon={["fas", "sync-alt"]} spin={isLoading} />
                </Button>
              )}
            </div>
          </div>

          {/* Filter bar */}
          <div
            className="d-flex align-items-center gap-2 mt-2 flex-wrap"
            style={{ paddingBottom: "4px" }}
          >
            <span style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: "500" }}>
              <FontAwesomeIcon icon={["fas", "filter"]} style={{ marginRight: "4px" }} />
              Filters:
            </span>

            <MultiSelectDropdown
              label="Rating"
              options={uniqueRatings}
              selected={selectedRatings}
              onChange={setSelectedRatings}
              renderOption={(val) => getRatingBadge(val)}
            />

            <MultiSelectDropdown
              label="Agent"
              options={uniqueAgents}
              selected={selectedAgents}
              onChange={setSelectedAgents}
            />

            {/* Comment filter toggle */}
            <div className="d-flex" style={{ gap: "2px" }}>
              {[
                { key: "all", label: "All" },
                { key: "with", label: "With Comments" },
                { key: "without", label: "Without" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  style={{
                    padding: "4px 10px",
                    fontSize: "0.75rem",
                    border: commentFilter === opt.key ? "1px solid #6366f1" : "1px solid #e2e8f0",
                    background: commentFilter === opt.key ? "#eef2ff" : "#f8fafc",
                    color: commentFilter === opt.key ? "#4f46e5" : "#64748b",
                    cursor: "pointer",
                    fontWeight: commentFilter === opt.key ? "600" : "400",
                    borderRadius:
                      opt.key === "all" ? "6px 0 0 6px" :
                      opt.key === "without" ? "0 6px 6px 0" : "0",
                  }}
                  onClick={() => setCommentFilter(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                style={{
                  padding: "4px 8px",
                  fontSize: "0.7rem",
                  border: "none",
                  background: "transparent",
                  color: "#ef4444",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Clear all
              </button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span style={{ color: "#64748b", fontSize: "0.85rem" }}>Loading feedback...</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon
                icon={["fas", "inbox"]}
                style={{ fontSize: "2rem", color: "#cbd5e1", marginBottom: "0.75rem" }}
              />
              <p style={{ color: "#94a3b8", fontSize: "0.85rem", margin: 0 }}>
                {searchTerm || activeFilterCount > 0
                  ? "No feedback matches your filters"
                  : "No feedback data available"}
              </p>
            </div>
          ) : (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table hover responsive className="mb-0" style={{ fontSize: "0.8rem" }}>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8fafc", zIndex: 1 }}>
                  <tr>
                    <th
                      style={{ cursor: "pointer", color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}
                      onClick={() => handleSort("ticket_id")}
                    >
                      Ticket ID <FontAwesomeIcon icon={["fas", getSortIcon("ticket_id")]} style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </th>
                    <th
                      style={{ cursor: "pointer", color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}
                      onClick={() => handleSort("rating")}
                    >
                      Rating <FontAwesomeIcon icon={["fas", getSortIcon("rating")]} style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </th>
                    <th
                      style={{ cursor: "pointer", color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}
                      onClick={() => handleSort("requester")}
                    >
                      Requester <FontAwesomeIcon icon={["fas", getSortIcon("requester")]} style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </th>
                    <th
                      style={{ cursor: "pointer", color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}
                      onClick={() => handleSort("agent")}
                    >
                      Agent <FontAwesomeIcon icon={["fas", getSortIcon("agent")]} style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </th>
                    <th
                      style={{ cursor: "pointer", color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}
                      onClick={() => handleSort("date")}
                    >
                      Date <FontAwesomeIcon icon={["fas", getSortIcon("date")]} style={{ fontSize: "10px", marginLeft: "4px" }} />
                    </th>
                    <th style={{ color: "#64748b", fontWeight: "600", borderBottom: "2px solid #e2e8f0", padding: "0.6rem 0.75rem" }}>
                      Comment
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr key={item.id || item.ticket_id || index}>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#334155", fontWeight: "500", borderBottom: "1px solid #f1f5f9" }}>
                        {item.ticket_id && subdomain ? (
                          <a
                            href={`https://${subdomain}.zendesk.com/agent/tickets/${item.ticket_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#4f46e5", textDecoration: "none" }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                          >
                            {item.ticket_id}
                          </a>
                        ) : (
                          item.ticket_id || "N/A"
                        )}
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", borderBottom: "1px solid #f1f5f9" }}>
                        {getRatingBadge(item.rating)}
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#475569", borderBottom: "1px solid #f1f5f9", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.requester_name || "N/A"}
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#475569", borderBottom: "1px solid #f1f5f9" }}>
                        {item.agent_name || "N/A"}
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#64748b", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                        {formatDate(item.received_date || item.date)}
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#475569", borderBottom: "1px solid #f1f5f9", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.comment || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

XSurveyFeed.propTypes = {
  feedbackData: PropTypes.array,
  isLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  globalDateRange: PropTypes.array,
  subdomain: PropTypes.string,
};

export default XSurveyFeed;
